"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { ui } from "@/components/ui";
import { authClient } from "@/lib/auth-client";

export function SignInForm({ next, reset }: { next: string; reset: boolean }) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setPending(true);
    setError(null);
    const { error } = await authClient.signIn.email({
      email: String(form.get("email")),
      password: String(form.get("password")),
    });
    if (error) {
      setPending(false);
      setError(
        error.status === 403
          ? "Please verify your email first. We just sent you a new link."
          : error.status === 429
            ? "Too many attempts. Wait a minute and try again."
            : "Wrong email or password.",
      );
      return;
    }
    router.push(next);
    router.refresh();
  }

  return (
    <form onSubmit={onSubmit} className={`${ui.card} space-y-4`}>
      <h1 className="text-xl font-semibold">Sign in</h1>
      {reset && !error && <p className={ui.notice}>Your password was changed. Sign in with the new one.</p>}
      {error && <p className={ui.error} role="alert">{error}</p>}
      <div>
        <label htmlFor="email" className={ui.label}>Email</label>
        <input id="email" name="email" type="email" required autoComplete="email" className={ui.input} />
      </div>
      <div>
        <div className="flex items-baseline justify-between">
          <label htmlFor="password" className={ui.label}>Password</label>
          <Link href="/forgot-password" className="text-xs text-zinc-600 hover:underline dark:text-zinc-400">
            Forgot password?
          </Link>
        </div>
        <input id="password" name="password" type="password" required autoComplete="current-password" className={ui.input} />
      </div>
      <button type="submit" disabled={pending} className={`${ui.button} w-full`}>
        {pending ? "Signing in…" : "Sign in"}
      </button>
      <p className="text-center text-sm text-zinc-600 dark:text-zinc-400">
        No account yet? <Link href="/sign-up" className={ui.link}>Create one</Link>
      </p>
    </form>
  );
}
